import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, PackageSearch, Copy, CheckCircle, Gift } from 'lucide-react';
import { ordersApi } from '@/lib/apiServices';
import { apiClient } from '@/lib/apiClient';
import { fmt } from '@/lib/format';
import { formatNumber } from '@/features/reports/lib/format';
import { formatJalaliDate } from '@/lib/format';
import { formatOrderNumber } from '@/lib/format';
import { getPackageDisplayName, getProductDisplayName } from '@/lib/packageDisplay';
import { persianDisplayText } from '@/lib/persianUi';
import OrderPaymentButton from './OrderPaymentButton';
import {
  getChequeStatusLabel,
  getPaymentStatusLabel,
  getSecureStatusLabel,
  getVendorAxisStatusLabel,
} from '../lib/orderStatus';
import OrderStatusTimeline, { OrderStatusBadge } from './OrderStatusTimeline';
import '../orders.css';

const PAGE_SIZE = 10;

function getErrorMessage(error) {
  console.error(error);
  return 'خطا در دریافت سفارش‌ها.';
}

const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback...', err);
    }
  }
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "absolute";
    textArea.style.left = "-999999px";
    document.body.prepend(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
    return true;
  } catch (error) {
    console.error('Fallback copy failed', error);
    return false;
  }
};


function OrderCard({ order, pkgLinks }) {
  const [copiedId, setCopiedId] = useState(null);

  const packagesInOrder = [];
  const seenPkgIds = new Set();
  order.items?.forEach(item => {
    if (item.simulation_package_id && !seenPkgIds.has(item.simulation_package_id)) {
      seenPkgIds.add(item.simulation_package_id);
      packagesInOrder.push({
        id: item.simulation_package_id,
        name: getPackageDisplayName(item.package_name),
        items: order.items.filter(i => i.simulation_package_id === item.simulation_package_id)
      });
    }
  });
  const isPackage = packagesInOrder.length > 0;

  const handleCopyCode = async (code, pkgId) => {
    if (!code) return;
    const success = await copyToClipboard(code);
    if (success) {
      setCopiedId(pkgId);
      setTimeout(() => setCopiedId(null), 2000);
    } else {
      alert('کپی کردن کد با خطا مواجه شد. لطفاً به صورت دستی کپی کنید.');
    }
  };

  const firstItem = order.items?.[0];
  const product = firstItem?.product;
  const otherProductCount = Math.max((order.items?.length ?? 0) - 1, 0);

  return (
    <article className="order-card">
      <div className="order-card-main">
        <div className="order-product-image">
          {product?.image_url ? (
            <img src={product.image_url} alt={persianDisplayText(product.name_fa, 'محصول')} />
          ) : (
            <PackageSearch aria-hidden="true" />
          )}
        </div>

        <div className="order-card-info">
          <div className="order-card-heading">
            <h2>سفارش {formatOrderNumber(order.order_number)}</h2>
            <OrderStatusBadge order={order} role="customer" />
          </div>
          <p className="order-card-product">
            {persianDisplayText(product?.name_fa, 'اقلام سفارش')}
            {otherProductCount > 0 && ` و ${formatNumber(otherProductCount)} محصول دیگر`}
          </p>
          <div className="order-card-item-statuses" aria-label="وضعیت اقلام سفارش">
            {order.items?.map((item) => (
              <span className="order-card-item-status" key={item.id}>
                {persianDisplayText(item.product?.name_fa, 'محصول')}: {getVendorAxisStatusLabel(
                  item.vendor_acceptance_status,
                  item.vendor_fulfillment_status,
                )}
              </span>
            ))}
          </div>
          <p className="order-card-meta">
            {order.created_at
              ? formatJalaliDate(order.created_at)
              : '—'}
          </p>
          <p className="order-card-total">
            مبلغ کل: <strong>{fmt(order.total_price_amount)} تومان</strong>
          </p>
          <div className="order-card-payment-statuses">
            <p className="order-payment-status">
              {getPaymentStatusLabel(order.payment_status)}
            </p>
            {order.secure_status && (
              <p className="order-payment-status">
                {getSecureStatusLabel(order.secure_status)}
              </p>
            )}
            {order.cheque_status && (
              <p className="order-payment-status">
                {getChequeStatusLabel(order.cheque_status)}
              </p>
            )}
          </div>
          {order.payment_status === 'unpaid' && (
            <OrderPaymentButton orderId={order.id} expiresAt={order.expires_at} />
          )}

            {/* === [CHANGED] قرارگیری کد معرف و لینک جزئیات در یک ستون در سمت چپ هدر === */}
            <div className="order-card-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.6rem' }}>
                {isPackage && packagesInOrder.length > 0 && pkgLinks[packagesInOrder[0].id] && (
                    <div style={{
                      background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: '8px', padding: '0.3rem 0.6rem', display: 'flex', alignItems: 'center',
                      gap: '0.5rem', flexWrap: 'wrap'
                    }}>
                      <span style={{ color: '#166534', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        کد معرف این پکیج:
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.85rem', color: '#1e293b', background: '#fff', padding: '2px 6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                          {pkgLinks[packagesInOrder[0].id]}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleCopyCode(pkgLinks[packagesInOrder[0].id], packagesInOrder[0].id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.3rem',
                              background: copiedId === packagesInOrder[0].id ? '#10b981' : '#059669', color: '#fff',
                              border: 'none', padding: '3px 8px', borderRadius: '4px',
                              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 'bold', transition: 'all 0.2s',
                            }}
                        >
                          {copiedId === packagesInOrder[0].id ? <><CheckCircle size={12} /> کپی شد</> : <><Copy size={12} /> کپی</>}
                        </button>
                      </div>
                    </div>
                )}

                {/* === [CHANGED] انتقال لینک جزئیات به اینجا === */}
                <Link
                    className="order-detail-link"
                    to={`/profile/orders/${order.id}`}
                    style={{ margin: 0, fontSize: '0.85rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                >
                  جزئیات سفارش <ArrowLeft size={14} aria-hidden="true" />
                </Link>
                {/* ====================================== */}
              </div>

            </div>

            {isPackage && packagesInOrder.map(pkg => (
                    <div key={pkg.id} style={{ marginTop: '1rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Gift size={16} color="#2563eb" /> {pkg.name}
                      </h3>
                      <ul style={{ margin: '0 0 0.75rem 1rem', padding: 0, fontSize: '0.85rem', color: '#475569' }}>
                        {pkg.items.map((item, idx) => (
                            <li key={idx}>{getProductDisplayName(item.product?.name_fa, item.product?.name_en)} (× {item.quantity})</li>
                        ))}
                      </ul>
                    </div>
                ))}

              <OrderStatusTimeline order={order} role="customer" variant="compact" />
          </div>
        </div>

        {/* لینک پایین کارت حذف شد چون به بالا منتقل شد */}

      </article>
  );
}

export default function OrderHistory() {
  const [view, setView] = useState('open');
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pkgLinks, setPkgLinks] = useState({});

  useEffect(() => {
    const fetchLinks = async () => {
      const pkgIds = [...new Set(orders.flatMap(o => o.items?.map(i => i.simulation_package_id).filter(Boolean)))];
      const newLinks = { ...pkgLinks };
      let updated = false;

      for (const pkgId of pkgIds) {
        if (newLinks[pkgId]) continue;
        try {
          const { data } = await apiClient.GET(`/api/v1/customer/referral/package-link/${pkgId}`);
          if (data?.code) {
            newLinks[pkgId] = data.code;
            updated = true;
          }
        } catch (e) {
          console.error("خطا در دریافت کد پکیج:", e);
        }
      }
      if (updated) setPkgLinks(newLinks);
    };
    if (orders.length > 0) fetchLinks();
  }, [orders, pkgLinks]);

  useEffect(() => {
    let active = true;
    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await ordersApi.mine({ view, page, size: PAGE_SIZE });
        if (!active) return;
        setOrders((current) => page === 1 ? data.items ?? [] : [...current, ...(data.items ?? [])]);
        setPages(data.pages ?? 0);
      } catch (requestError) {
        if (active) setError(getErrorMessage(requestError));
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchOrders();
    return () => { active = false; };
  }, [view, page]);

  const changeView = (nextView) => {
    if (nextView === view) return;
    setOrders([]);
    setPage(1);
    setPages(0);
    setError('');
    setView(nextView);
  };

  return (
      <section className="orders-page" aria-labelledby="orders-title">
        <header className="orders-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h1 id="orders-title" style={{ margin: 0 }}>سفارش‌های من</h1>
          </div>

          <div className="order-view-chips" role="tablist" aria-label="نمایش سفارش‌ها" style={{ margin: 0 }}>
            <button
                aria-selected={view === 'open'}
                className={view === 'open' ? 'is-active' : ''}
                onClick={() => changeView('open')}
                role="tab"
                type="button"
            >
              سفارش‌های جاری
            </button>
            <button
                aria-selected={view === 'history'}
                className={view === 'history' ? 'is-active' : ''}
                onClick={() => changeView('history')}
                role="tab"
                type="button"
            >
              تاریخچه سفارش‌ها
            </button>
          </div>
        </header>

        {error && (
            <div className="orders-state orders-state--error" role="alert">
              {error}
            </div>
        )}

        {!error && loading && orders.length === 0 && (
            <div className="orders-state" role="status">در حال دریافت سفارش‌ها...</div>
        )}

        {!error && !loading && orders.length === 0 && (
            <div className="orders-state">
              <PackageSearch size={32} aria-hidden="true" />
              <p>
                {view === 'open'
                    ? 'در حال حاضر سفارش جاری ندارید.'
                    : 'هنوز سفارشی در تاریخچه شما ثبت نشده است.'}
              </p>
            </div>
        )}

        {orders.length > 0 && (
            <div className="order-list">
              {orders.map((order) => (
                  <OrderCard key={order.id} order={order} pkgLinks={pkgLinks} />
              ))}
            </div>
        )}

        {loading && orders.length > 0 && (
            <div className="orders-state" role="status">در حال دریافت سفارش‌های بیشتر...</div>
        )}

        {!loading && !error && page < pages && (
            <button
                className="orders-load-more"
                onClick={() => setPage((current) => current + 1)}
                type="button"
            >
              نمایش سفارش‌های بیشتر
            </button>
        )}
      </section>
  );
}